import { Stack, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { FC, useCallback, useMemo, useState } from 'react';

import { VirtualCheckboxList } from './virtual-checkbox-list';

interface VirtualSearchableCheckboxListProps {
  options: { value: string; label: string }[];
  selectedIds: number[];
  onSelectedIdsChange: (ids: number[]) => void;
  searchPlaceholder: string;
  emptyLabel?: string;
  searchingLabel?: string;
  noResultsLabel?: string;
}

function filterOptionsBySearch(
  options: { id: number; label: string }[],
  search: string,
): { id: number; label: string }[] {
  const query = search.trim().toLowerCase();
  if (!query) {
    return options;
  }

  return options.filter((option) => option.label.toLowerCase().includes(query));
}

export const VirtualSearchableCheckboxList: FC<VirtualSearchableCheckboxListProps> = ({
  options,
  selectedIds,
  onSelectedIdsChange,
  searchPlaceholder,
  emptyLabel = '—',
  searchingLabel,
  noResultsLabel = '—',
}) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 200);

  const normalizedOptions = useMemo(
    () =>
      options.map((option) => ({
        id: Number(option.value),
        label: option.label,
      })),
    [options],
  );

  const filteredOptions = useMemo(
    () => filterOptionsBySearch(normalizedOptions, debouncedSearch),
    [debouncedSearch, normalizedOptions],
  );

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const isSearchPending = search !== debouncedSearch;

  const toggleOption = useCallback(
    (id: number) => {
      onSelectedIdsChange(
        selectedSet.has(id)
          ? selectedIds.filter((value) => value !== id)
          : [...selectedIds, id],
      );
    },
    [onSelectedIdsChange, selectedIds, selectedSet],
  );

  return (
    <Stack gap="xs">
      <TextInput
        onChange={(event) => setSearch(event.currentTarget.value)}
        placeholder={searchPlaceholder}
        size="xs"
        value={search}
      />
      {!options.length ? (
        <Text c="dimmed" size="xs">
          {emptyLabel}
        </Text>
      ) : isSearchPending && searchingLabel ? (
        <Text c="dimmed" size="xs">
          {searchingLabel}
        </Text>
      ) : !filteredOptions.length ? (
        <Text c="dimmed" size="xs">
          {noResultsLabel}
        </Text>
      ) : (
        <VirtualCheckboxList
          items={filteredOptions}
          key={debouncedSearch}
          onToggle={toggleOption}
          selectedIds={selectedSet}
        />
      )}
    </Stack>
  );
};
